import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send, Users, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";
import Icon from "../components/Icon";
import SocialLinks from "../components/SocialLinks";
import ToastWithAiBadge from "../components/ToastWithAiBadge";
import { COMPANY, IMAGES } from "../data/content";
import { useI18n } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Kontakt() {
  const { t } = useI18n();
  const k = t.kontakt;
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(<ToastWithAiBadge>{k.form.consentError}</ToastWithAiBadge>);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(<ToastWithAiBadge>{k.form.success}</ToastWithAiBadge>);
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(<ToastWithAiBadge>{k.form.error}</ToastWithAiBadge>);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-kontakt">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#eef2f7]" data-testid="kontakt-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.contactEngineers})` }} />
        <div className="absolute inset-0 hero-fade-left" />
        <div className="relative max-w-[1320px] mx-auto px-5 lg:px-8 pt-32 pb-24">
          <div className="max-w-xl animate-fade-up">
            <p className="text-blue-brand font-head font-bold uppercase tracking-[0.18em] text-sm mb-4">{k.hero.label}</p>
            <h1 className="font-head font-extrabold text-ink text-4xl sm:text-5xl lg:text-[3.2rem] leading-[1.05] mb-6">
              {k.hero.titleLines.map((l, i) => (
                <React.Fragment key={i}>
                  {l}
                  {i < k.hero.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </h1>
            <p className="text-ink-soft text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              {k.hero.paragraph}
            </p>
          </div>
        </div>
      </section>

      {/* Contact data + form */}
      <section className="py-20 bg-white" data-testid="kontakt-main">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          {/* Left: data */}
          <div>
            <h2 className="text-blue-brand font-head font-bold uppercase tracking-[0.18em] text-sm mb-8">{k.dataTitle}</h2>
            <ul className="space-y-7">
              <li className="flex gap-4">
                <div className="w-11 h-11 rounded-full border-2 border-blue-100 flex items-center justify-center text-blue-brand shrink-0">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="font-head font-bold text-ink">{COMPANY.name}</p>
                  <p className="text-muted-foreground">{COMPANY.zip}</p>
                </div>
              </li>
              <li className="flex gap-4 items-center">
                <div className="w-11 h-11 rounded-full border-2 border-blue-100 flex items-center justify-center text-blue-brand shrink-0">
                  <Phone size={20} />
                </div>
                <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="text-ink hover:text-blue-brand transition-colors">{COMPANY.phone}</a>
              </li>
              <li className="flex gap-4 items-center">
                <div className="w-11 h-11 rounded-full border-2 border-blue-100 flex items-center justify-center text-blue-brand shrink-0">
                  <Mail size={20} />
                </div>
                <a href={`mailto:${COMPANY.email}`} className="text-ink hover:text-blue-brand transition-colors">{COMPANY.email}</a>
              </li>
              <li className="flex gap-4 items-center">
                <div className="w-11 h-11 rounded-full border-2 border-blue-100 flex items-center justify-center text-blue-brand shrink-0">
                  <Globe size={20} />
                </div>
                <a href={`https://${COMPANY.web}`} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-blue-brand transition-colors">{COMPANY.web}</a>
              </li>
              <li className="flex gap-4">
                <div className="w-11 h-11 rounded-full border-2 border-blue-100 flex items-center justify-center text-blue-brand shrink-0">
                  <Clock size={20} />
                </div>
                <div className="text-ink">
                  {k.hours.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </li>
            </ul>

            <div className="mt-10 pt-8 border-t border-line">
              <Users size={28} className="text-blue-brand mb-3" strokeWidth={1.6} />
              <h3 className="font-head font-bold text-ink uppercase tracking-wide text-sm mb-2">{k.personalTitle}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-sm">
                {k.personalText}
              </p>
              <div className="mt-6">
                <p className="font-head font-bold text-ink uppercase tracking-wide text-sm mb-3">{k.followUs}</p>
                <SocialLinks variant="light" />
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white border border-line rounded-2xl shadow-xl shadow-blue-900/5 p-7 lg:p-9" data-testid="kontakt-form-card">
            <h3 className="font-head font-bold text-ink uppercase tracking-wide text-lg mb-6">{k.formTitle}</h3>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" required value={form.name} onChange={update("name")} placeholder={k.form.name} className="w-full border border-line rounded-lg px-4 py-3 outline-none focus:border-blue-brand transition-colors" />
                <input data-testid="contact-email" required type="email" value={form.email} onChange={update("email")} placeholder={k.form.email} className="w-full border border-line rounded-lg px-4 py-3 outline-none focus:border-blue-brand transition-colors" />
              </div>
              <input data-testid="contact-company" value={form.company} onChange={update("company")} placeholder={k.form.company} className="w-full border border-line rounded-lg px-4 py-3 outline-none focus:border-blue-brand transition-colors" />
              <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder={k.form.phone} className="w-full border border-line rounded-lg px-4 py-3 outline-none focus:border-blue-brand transition-colors" />
              <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={update("message")} placeholder={k.form.message} className="w-full border border-line rounded-lg px-4 py-3 outline-none focus:border-blue-brand transition-colors resize-y" />
              <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                <input data-testid="contact-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#2563eb] w-4 h-4" />
                <span>{k.form.consentPre}<Link to="/datenschutz" className="text-blue-brand underline">{k.form.consentLink}</Link>{k.form.consentPost}</span>
              </label>
              <button data-testid="contact-submit" type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-blue-brand text-white font-semibold px-7 py-3.5 rounded-full hover:bg-blue-700 transition-colors disabled:opacity-60 w-full sm:w-auto">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {k.form.submit}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="py-14 section-soft" data-testid="kontakt-values">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-10">
          {k.valueProps.map((v) => (
            <div key={v.title}>
              <Icon name={v.icon} size={32} className="text-blue-brand mb-4" strokeWidth={1.5} />
              <h3 className="font-head font-bold text-ink text-[14px] uppercase tracking-wide mb-2">{v.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Einsatzgebiet */}
      <section className="py-20 bg-[#081628]" data-testid="kontakt-map">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <p className="text-center text-blue-300 font-head font-bold uppercase tracking-[0.18em] text-sm mb-2">{k.mapLabel}</p>
          <h2 className="text-center font-head font-extrabold text-white text-3xl md:text-4xl mb-12">{k.mapTitle}</h2>

          <div className="relative w-full max-w-5xl mx-auto aspect-[3/2] rounded-2xl overflow-hidden border border-blue-500/30"
            style={{ backgroundColor: "#04101f" }}
          >
            {/* hologram world map background */}
            <div
              className="absolute inset-0 bg-cover bg-center opacity-90"
              style={{ backgroundImage: `url(${IMAGES.worldMap})` }}
            />
            {(() => {
              const origin = { x: 53.4, y: 33.6 };
              const dests = [
                { x: 20, y: 31 },
                { x: 34, y: 66 },
                { x: 78, y: 36 },
                { x: 59, y: 38 },
              ];
              return (
                <>
                  <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                    {dests.map((d, i) => (
                      <line key={i} x1={`${origin.x}%`} y1={`${origin.y}%`} x2={`${d.x}%`} y2={`${d.y}%`}
                        stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="5 6" opacity="0.65" />
                    ))}
                  </svg>
                  {dests.map((d, i) => (
                    <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: `${d.x}%`, top: `${d.y}%` }} data-testid={`map-dest-${i}`}>
                      <span className="block w-2.5 h-2.5 rounded-full bg-sky-400 ring-4 ring-sky-400/25" />
                    </div>
                  ))}
                  <div className="absolute -translate-x-1/2 -translate-y-1/2 z-20" style={{ left: `${origin.x}%`, top: `${origin.y}%` }} data-testid="map-origin-muenchen">
                    <div className="flex flex-col items-center">
                      <span className="w-4 h-4 rounded-full bg-blue-500 ring-4 ring-blue-400/40 animate-pulse" />
                      <span className="mt-1 text-[12px] font-bold text-white bg-blue-600/90 px-2 py-0.5 rounded whitespace-nowrap">{k.origin}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          <p className="text-center text-blue-100/70 text-sm mt-8">
            {k.mapNote}
          </p>
        </div>
      </section>
    </div>
  );
}
